// utils/roofEdgesUtils.js
import * as THREE from "three";

// Reużywalne obiekty (żeby nie alokować w pętli)
const _raycaster = new THREE.Raycaster();
const _down = new THREE.Vector3(0, -1, 0);

// Tymczasowy mesh do raycastu z DoubleSide (raycast respektuje material.side)
const _tmpMat = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide });
const _tmpMesh = new THREE.Mesh(undefined, _tmpMat);
_tmpMesh.matrixAutoUpdate = false;

/**
 * Zwraca TYLKO obrys zewnętrzny dachu jako prostokąt (okap),
 * bez żadnych wewnętrznych krawędzi (kalenica / triangulacja / seams).
 *
 * Zwraca w WORLD:
 *  - segments: [{a: Vector3, b: Vector3}, ...] (4 odcinki)
 *  - verts: [Vector3, ...] (4 narożniki)
 */
export function getRoofPerimeterWorld(mesh, options = {}) {
  const { _debug = false } = options;

  if (!mesh?.geometry?.attributes?.position) {
    return { segments: [], verts: [] };
  }

  // upewnij się, że matrixWorld jest aktualny
  mesh.updateWorldMatrix(true, false);

  const geom = mesh.geometry;
  const pos = geom.attributes.position;

  // 1) Zbierz wierzchołki w WORLD + policz AABB (okap)
  let minX = Infinity, maxX = -Infinity;
  let minZ = Infinity, maxZ = -Infinity;
  let minY = Infinity, maxY = -Infinity;

  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(mesh.matrixWorld);

    if (v.x < minX) minX = v.x;
    if (v.x > maxX) maxX = v.x;
    if (v.z < minZ) minZ = v.z;
    if (v.z > maxZ) maxZ = v.z;

    if (v.y < minY) minY = v.y;
    if (v.y > maxY) maxY = v.y;
  }

  if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minZ) || !isFinite(maxZ)) {
    return { segments: [], verts: [] };
  }

  // 2) Raycast helper: punkt na dachu dla XZ (górna połać)
  _tmpMesh.geometry = geom;
  _tmpMesh.matrixWorld.copy(mesh.matrixWorld);

  const heightSpan = Math.max(1e-3, maxY - minY);
  const startY = maxY + heightSpan * 2 + 1; // start wysoko ponad dach

  const raycastTopAtXZ = (x, z) => {
    _raycaster.set(new THREE.Vector3(x, startY, z), _down);
    const hits = _raycaster.intersectObject(_tmpMesh, false);
    if (hits && hits.length) return hits[0].point.clone();
    return null;
  };

  // 3) Fallback (gdyby raycast nie trafił): wybierz najbliższy w XZ, ale preferuj większe Y
  const pickCornerFallback = (x, z) => {
    let best = null;
    let bestD = Infinity;
    let bestY = -Infinity;

    const vv = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      vv.set(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(mesh.matrixWorld);
      const dx = vv.x - x;
      const dz = vv.z - z;
      const d = dx * dx + dz * dz;

      // 1) mniejszy dystans
      // 2) przy podobnym dystansie bierz wyższy punkt (górna połać > spód)
      if (d < bestD - 1e-10 || (Math.abs(d - bestD) <= 1e-10 && vv.y > bestY)) {
        bestD = d;
        bestY = vv.y;
        best = vv.clone();
      }
    }
    return best || new THREE.Vector3(x, maxY, z);
  };

  const cornerPoint = (x, z) => raycastTopAtXZ(x, z) || pickCornerFallback(x, z);

  // 4) 4 narożniki prostokąta (po obwodzie)
  const c1 = cornerPoint(minX, minZ);
  const c2 = cornerPoint(minX, maxZ);
  const c3 = cornerPoint(maxX, maxZ);
  const c4 = cornerPoint(maxX, minZ);

  const verts = [c1, c2, c3, c4];

  // 5) segmenty obwodu (tylko 4, zero wewnętrznych)
  const segments = [
    { a: c1.clone(), b: c2.clone() },
    { a: c2.clone(), b: c3.clone() },
    { a: c3.clone(), b: c4.clone() },
    { a: c4.clone(), b: c1.clone() },
  ];

  if (_debug) {
    // eslint-disable-next-line no-console
    console.log("[getRoofPerimeterWorld] bbox:", { minX, maxX, minZ, maxZ, minY, maxY });
  }

  return { segments, verts };
}
